import { z } from "zod";
import {
  APPROVAL_DECISIONS,
  DEVICE_TYPES,
  INDIAN_PHONE_REGEX,
  PERSONAS,
  PLATFORMS,
  PRIORITIES,
  PROJECT_STATUSES,
  ROLES,
  TASK_STATUSES,
} from "./constants";

const trimmed = (max: number) => z.string().trim().min(1, "Required").max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v === undefined ? undefined : v || null));
const isoDate = z
  .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD"), z.literal("")])
  .optional()
  .nullable()
  .transform((v) => (v === undefined ? undefined : v || null));

/** Normalises "98765 43210", "+91-98765-43210", "09876543210" → "+919876543210". */
export function normalizeIndianPhone(input: string): string {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `+91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return input.trim();
}

export const phoneSchema = z
  .string()
  .trim()
  .transform(normalizeIndianPhone)
  .refine((v) => INDIAN_PHONE_REGEX.test(v), "Enter a valid Indian mobile number (+91 XXXXX XXXXX)");

export const passwordSchema = z
  .string()
  .min(8, "At least 8 characters")
  .max(128, "Too long")
  .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), "Use letters and numbers");

// ---------- Auth ----------
export const registerSchema = z.object({
  name: trimmed(80),
  email: z.string().trim().toLowerCase().email("Enter a valid email"),
  password: passwordSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email"),
  password: z.string().min(1, "Required").max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: z.string().trim().toLowerCase().email("Enter a valid email") });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({ token: z.string().min(10).max(1000), password: passwordSchema });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const otpRequestSchema = z.object({ phone: phoneSchema });
export const otpVerifySchema = z.object({
  phone: phoneSchema,
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
  name: z.string().trim().max(80).optional(),
});

// ---------- Organisation ----------
export const createOrgSchema = z.object({
  name: trimmed(100),
  persona: z.enum(PERSONAS).optional(),
});
export type CreateOrgInput = z.infer<typeof createOrgSchema>;

export const updateOrgSchema = z.object({
  name: trimmed(100).optional(),
  brandColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  timezone: z.string().max(64).optional(),
});

export const createWorkspaceSchema = z.object({ name: trimmed(80) });

// ---------- Clients ----------
export const clientSchema = z.object({
  name: trimmed(120),
  contactName: optionalText(80),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .optional()
    .nullable()
    .or(z.literal("").transform(() => null)),
  phone: phoneSchema.optional().nullable().or(z.literal("").transform(() => null)),
  company: optionalText(120),
});
export type ClientInput = z.infer<typeof clientSchema>;

// ---------- Projects ----------
export const projectSchema = z.object({
  name: trimmed(120),
  workspaceId: z.string().uuid().optional(),
  clientId: z.string().uuid().optional().nullable(),
  description: optionalText(2000),
  startDate: isoDate,
  dueDate: isoDate,
  status: z.enum(PROJECT_STATUSES).default("planning"),
});
export type ProjectInput = z.input<typeof projectSchema>;
export const updateProjectSchema = projectSchema.partial();

// ---------- Websites ----------
export const websiteSchema = z.object({
  name: trimmed(120),
  url: z
    .string()
    .trim()
    .url("Enter a full URL including https://")
    .refine((v) => /^https?:\/\//i.test(v), "Only http(s) URLs are supported"),
  projectId: z.string().uuid(),
  platform: z.enum(PLATFORMS).default("other"),
});
export type WebsiteInput = z.input<typeof websiteSchema>;
export const updateWebsiteSchema = websiteSchema.omit({ projectId: true }).partial();

export const reviewLinkSchema = z.object({
  websiteId: z.string().uuid(),
  label: optionalText(80),
  expiresInDays: z.number().int().min(1).max(365).optional().nullable(),
  allowAnonymous: z.boolean().default(false),
});

export const versionSchema = z.object({ label: optionalText(80), notes: optionalText(1000) });

// ---------- Feedback ----------
export const rectSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const anchorSchema = z.object({
  selector: z.string().max(2000).nullable(),
  xpath: z.string().max(2000).nullable(),
  tag: z.string().max(40).nullable(),
  classes: z.array(z.string().max(120)).max(30).default([]),
  elementId: z.string().max(200).nullable().optional(),
  text: z.string().max(300).nullable(),
  label: z.string().max(120).nullable().optional(),
  rect: rectSchema.nullable(),
  /** Click offset inside the element, 0..1. */
  relX: z.number().min(0).max(1),
  relY: z.number().min(0).max(1),
  /** Absolute document coordinates — last-resort fallback. */
  pageX: z.number(),
  pageY: z.number(),
  documentWidth: z.number().optional(),
  documentHeight: z.number().optional(),
});
export type Anchor = z.infer<typeof anchorSchema>;

export const contextSchema = z.object({
  url: z.string().max(2000),
  path: z.string().max(2000),
  title: z.string().max(300).default(""),
  browser: z.string().max(80).default("Unknown"),
  os: z.string().max(80).default("Unknown"),
  deviceType: z.enum(DEVICE_TYPES).default("desktop"),
  viewport: z.object({ width: z.number().int(), height: z.number().int() }),
  screen: z.object({ width: z.number().int(), height: z.number().int() }),
  devicePixelRatio: z.number().default(1),
  userAgent: z.string().max(500).default(""),
  timestamp: z.string().max(40),
});
export type FeedbackContext = z.infer<typeof contextSchema>;

export const createPublicFeedbackSchema = z.object({
  comment: trimmed(5000),
  priority: z.enum(PRIORITIES).default("medium"),
  anchor: anchorSchema,
  context: contextSchema,
  /** Upload ids from POST /public/review/:token/uploads. */
  attachmentIds: z.array(z.string().uuid()).max(10).default([]),
  screenshotId: z.string().uuid().optional().nullable(),
});
export type CreatePublicFeedbackInput = z.input<typeof createPublicFeedbackSchema>;

export const updateFeedbackSchema = z.object({
  status: z.enum(TASK_STATUSES).optional(),
  priority: z.enum(PRIORITIES).optional(),
  title: trimmed(200).optional(),
  dueDate: isoDate,
});

export const commentSchema = z.object({
  body: trimmed(5000),
  attachmentIds: z.array(z.string().uuid()).max(10).default([]),
  internal: z.boolean().default(false),
});

export const assignSchema = z.object({
  userIds: z.array(z.string().uuid()).max(10),
});

export const identifySchema = z.object({
  name: trimmed(80),
  email: z.string().trim().toLowerCase().email("Enter a valid email"),
});
export type IdentifyInput = z.infer<typeof identifySchema>;

export const approvalSchema = z.object({
  decision: z.enum(APPROVAL_DECISIONS),
  message: optionalText(2000),
  pageUrl: z.string().max(2000).optional().nullable(),
});
export type ApprovalInput = z.input<typeof approvalSchema>;

// ---------- Tasks ----------
export const createTaskSchema = z.object({
  projectId: z.string().uuid(),
  title: trimmed(200),
  description: optionalText(5000),
  priority: z.enum(PRIORITIES).default("medium"),
  status: z.enum(TASK_STATUSES).default("open"),
  assigneeIds: z.array(z.string().uuid()).max(10).default([]),
  dueDate: isoDate,
});

export const moveTaskSchema = z.object({
  status: z.enum(TASK_STATUSES),
  /** Fractional index used to order cards in a column. */
  position: z.number(),
});

// ---------- Team ----------
export const inviteSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: z.enum(ROLES).refine((r) => r !== "owner", "Owners cannot be invited"),
  name: optionalText(80),
});
export const updateMemberSchema = z.object({
  role: z.enum(ROLES).refine((r) => r !== "owner", "Ownership transfer is not supported here"),
});

export const onboardingSchema = z.object({
  persona: z.enum(PERSONAS).optional(),
  step: z.number().int().min(0).max(5).optional(),
  completed: z.boolean().optional(),
});

export const MOCKUP_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "application/pdf"] as const;

export const createMockupSchema = z.object({
  projectId: z.string().uuid(),
  /** Id from POST /uploads. */
  fileId: z.string().uuid(),
  name: trimmed(120),
  pageCount: z.number().int().min(1).max(200).default(1),
  width: z.number().int().min(1).max(100_000).nullable().optional(),
  height: z.number().int().min(1).max(100_000).nullable().optional(),
});
export type CreateMockupInput = z.input<typeof createMockupSchema>;

export const mockupPinSchema = z.object({
  comment: trimmed(5000),
  priority: z.enum(PRIORITIES).default("medium"),
  page: z.number().int().min(1).max(200).default(1),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  viewport: z.object({ width: z.number().int().min(0).max(100_000), height: z.number().int().min(0).max(100_000) }).optional(),
});
export type MockupPinInput = z.input<typeof mockupPinSchema>;
